import React, { useMemo, useState } from 'react';
import { Box, Typography, Checkbox, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ADDITIONAL_FILTERS, FILTER_COLORS } from './constants';

interface AdditionalFiltersPanelProps {
  selectedFilters: string[];
  onToggleFilter: (filterId: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
}

const CustomUncheckedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="18" height="18" rx="3" stroke="#6B6B6B" strokeWidth="1" fill="none" />
  </svg>
);

const CustomCheckedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="18" height="18" rx="3" stroke="#39ACE7" strokeWidth="1" fill="none" />
    <path 
      d="M5.5 10L8.5 13L14.5 7" 
      stroke="#39ACE7" 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
    />
  </svg>
);

const AdditionalFiltersPanel: React.FC<AdditionalFiltersPanelProps> = ({
  selectedFilters,
  onToggleFilter,
  onToggleAll,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredItems = useMemo(() => {
    let items = ADDITIONAL_FILTERS;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter((item: any) => 
        item.label.toLowerCase().includes(query)
      );
    }

    // Filter out level 2 items whose parent is not selected
    return items.filter((item: any) => {
      // Always show 'all' and level 1 items
      if (item.id === 'all' || item.level === 1) {
        return true;
      }
      
      // For level 2 items, only show if parent is selected
      if (item.level === 2 && item.parent) {
        return selectedFilters.includes(item.parent);
      }
      
      return true;
    });
  }, [searchQuery, selectedFilters]);

  // Get all filter IDs (excluding 'all')
  const allFilterIds = ADDITIONAL_FILTERS.filter(item => item.id !== 'all').map(item => item.id);
  
  // Count how many filters are selected (excluding 'all')
  const selectedCount = selectedFilters.filter(id => id !== 'all').length;
  const totalFilters = allFilterIds.length;
  
  // Check if ALL filters are selected
  const allSelected = selectedCount === totalFilters && totalFilters > 0;

  return (
    <Box
      className="advance-filter-additional-panel"
      sx={{
        position: 'absolute',
        left: '26px',
        top: '44px',
        width: '371px',
        maxHeight: '790px',
        backgroundColor: FILTER_COLORS.panelBg,
        borderRadius: '0px 4px 4px 4px',
        boxShadow: FILTER_COLORS.overlayShadow,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '10px 15px',
          boxShadow: FILTER_COLORS.overlayShadow,
          backgroundColor: FILTER_COLORS.panelBg,
        }}
      >
        <SearchIcon sx={{ color: FILTER_COLORS.borderIndent, fontSize: 20 }} />
        <TextField
          placeholder="Search Filters"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="standard"
          fullWidth
          InputProps={{
            disableUnderline: true,
            sx: {
              fontFamily: 'Montserrat',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '25px',
              color: '#000000',
              '& input': {
                color: '#000000 !important',
              },
              '& input::placeholder': {
                color: FILTER_COLORS.borderIndent,
                opacity: 1,
              },
            },
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px 24px 24px',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: FILTER_COLORS.scrollbar },
          '&::-webkit-scrollbar-thumb': { background: FILTER_COLORS.accent, borderRadius: '3px' },
        }}
      >
        {filteredItems.map((item: any) => {
          // For 'all' item, use the calculated allSelected state
          // For other items, check if they're in selectedFilters
          const isChecked = item.id === 'all' 
            ? allSelected 
            : selectedFilters.includes(item.id);
          
          const isNested = item.level === 2;

          return (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                paddingLeft: isNested ? '40px' : 0,
                marginBottom: '16px',
                minHeight: '24px',
                position: 'relative',
                ...(isNested && {
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: '23px',
                    top: '-6px',
                    width: '1px',
                    height: '18px',
                    backgroundColor: FILTER_COLORS.placeholder,
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: '23px',
                    top: '12px',
                    width: '12px',
                    height: '1px',
                    backgroundColor: FILTER_COLORS.placeholder,
                  },
                }),
              }}
            >
              <Checkbox
                checked={isChecked}
                onChange={(e) => {
                  if (item.id === 'all') {
                    onToggleAll(e.target.checked);
                  } else {
                    onToggleFilter(item.id, e.target.checked);
                  }
                }}
                icon={<CustomUncheckedIcon />}
                checkedIcon={<CustomCheckedIcon />}
                sx={{
                  padding: 0,
                  margin: 0,
                  '&.MuiCheckbox-root': {
                    padding: 0,
                    margin: 0,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: 'transparent',
                  },
                  '& .MuiSvgIcon-root': {
                    fill: 'none',
                  },
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '18px',
                  color: FILTER_COLORS.label,
                  margin: 0,
                  padding: 0,
                }}
              >
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AdditionalFiltersPanel;